<template>
  <div
    class="rank-badge"
    :class="rankDetails.id"
    :style="{ '--size': `${size}px` }"
    :title="$t('coach.renown_rank.title', { rank: rankDetails.displayName, elo: formattedElo })"
  >
    <img
      :src="rankDetails.iconUrl"
      :alt="$t('coach.renown_rank.alt', { rank: rankDetails.displayName })"
      :width="size"
      :height="size"
      class="rank-icon"
    >
    <span class="rank-elo">{{ formattedElo }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  rank: {
    type: Number,
    required: true,
  },
  size: {
    type: Number,
    default: 32,
  },
});

// Renown rank definitions
const renownRanks = [
  {
    id: 'bronze',
    displayName: 'Bronze',
    iconUrl: 'https://cdn.renown.gg/images/ranks/bronze.svg',
    min: 0,
    max: 6999,
    backgroundColor: 'rgb(209, 166, 110)',
    textColor: 'rgb(129, 84, 36)',
  },
  {
    id: 'silver',
    displayName: 'Silver',
    iconUrl: 'https://cdn.renown.gg/images/ranks/silver.svg',
    min: 7000,
    max: 10999,
    backgroundColor: 'rgb(220, 229, 232)',
    textColor: 'rgb(138, 168, 181)',
  },
  {
    id: 'gold',
    displayName: 'Gold',
    iconUrl: 'https://cdn.renown.gg/images/ranks/gold.svg',
    min: 11000,
    max: 14999,
    backgroundColor: 'rgb(245, 203, 135)',
    textColor: 'rgb(225, 95, 0)',
  },
  {
    id: 'platinum',
    displayName: 'Platinum',
    iconUrl: 'https://cdn.renown.gg/images/ranks/platinum.svg',
    min: 15000,
    max: 18999,
    backgroundColor: 'rgb(26, 207, 207)',
    textColor: 'rgb(33, 116, 116)',
  },
  {
    id: 'diamond',
    displayName: 'Diamond',
    iconUrl: 'https://cdn.renown.gg/images/ranks/diamond.svg',
    min: 19000,
    max: 21999,
    backgroundColor: 'rgb(164, 201, 244)',
    textColor: 'rgb(69, 109, 248)',
  },
  {
    id: 'master',
    displayName: 'Master',
    iconUrl: 'https://cdn.renown.gg/images/ranks/master.svg',
    min: 22000,
    max: 24999,
    backgroundColor: 'rgb(228, 194, 249)',
    textColor: 'rgb(199, 74, 243)',
  },
  {
    id: 'renowned',
    displayName: 'Renowned',
    iconUrl: 'https://cdn.renown.gg/images/ranks/renowned.svg',
    min: 25000,
    max: Infinity,
    backgroundColor: 'rgb(31, 232, 99)',
    textColor: 'rgb(16, 104, 46)',
  },
];

const rankDetails = computed(() => {
  for (const rank of renownRanks) {
    if (props.rank >= rank.min && props.rank <= rank.max) {
      return rank;
    }
  }
  return renownRanks[renownRanks.length - 1]; // Default to highest rank
});

const formattedElo = computed(() => props.rank.toLocaleString('en-US'));
</script>

<style scoped>
.rank-badge {
    display: flex;
    align-items: center;
    border-radius: 100px;
    font-weight: 600;
    min-width: calc(var(--size) * 3.25);
    font-size: calc((var(--size) / 32) * 18);
}

.rank-icon {
    margin-left: calc(var(--size) * -0.25);
}

.rank-elo {
    display: flex;
    flex-grow: 1;
    justify-content: center;
    padding-right: calc(var(--size) * 0.23);
}

/* Bronze */
.rank-badge.bronze {
    background-color: rgb(209, 166, 110);
    color: rgb(129, 84, 36);
}

/* Silver */
.rank-badge.silver {
    background-color: rgb(220, 229, 232);
    color: rgb(138, 168, 181);
}

/* Gold */
.rank-badge.gold {
    background-color: rgb(245, 203, 135);
    color: rgb(225, 95, 0);
}

/* Platinum */
.rank-badge.platinum {
    background-color: rgb(26, 207, 207);
    color: rgb(33, 116, 116);
}

/* Diamond */
.rank-badge.diamond {
    background-color: rgb(164, 201, 244);
    color: rgb(69, 109, 248);
}

/* Master */
.rank-badge.master {
    background-color: rgb(228, 194, 249);
    color: rgb(199, 74, 243);
}

/* Renowned */
.rank-badge.renowned {
    background-color: rgb(31, 232, 99);
    color: rgb(16, 104, 46);
}
</style>
